import {
  DIRECT_JOBS_MW_RANGE_MODEL_VERSION,
  DIRECT_JOBS_MODEL_VERSION,
  ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
  type DirectJobsScreeningInput,
  type DirectJobsMwRangeScreeningResult,
  type DirectJobsScreeningResult,
  type EconomicSourceCatalogSnapshot,
  type MwRangeQuantity,
} from '../../types/economicContribution';
import { effectiveCatalogLifecycle, findRuntimeCoefficient } from '../../data/economicContribution/sourceCatalog';
import { bandForMw, finiteNonNegative, finitePositive, hashEconomicModelInput, roundEconomic } from './modelUtils';
import { staffingBasisFactor, staffingBasisLimitation } from './powerBasisDisclosure';

function emptyResult(
  input: DirectJobsScreeningInput,
  catalog: EconomicSourceCatalogSnapshot,
  unavailableReason: NonNullable<DirectJobsScreeningResult['unavailableReason']>,
  limitations: readonly string[],
): DirectJobsScreeningResult {
  return {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    modelVersion: DIRECT_JOBS_MODEL_VERSION,
    status: 'not-calculated',
    siteId: input.siteId,
    range: null,
    evidenceBasis: null,
    method: null,
    facilityOperationsRange: null,
    itAndSiteOperationsRange: null,
    contractedServicesRange: null,
    factors: [],
    limitations,
    unavailableReason,
    modelInputHash: hashEconomicModelInput({ input, catalogVersion: catalog.catalogVersion }),
    sourceCatalogVersion: catalog.catalogVersion,
    sourceEntryIds: [],
    trace: [],
  };
}

/**
 * Deterministic direct-onsite employment screen. It applies no operating-model,
 * workload, rack, building, or cooling coefficient: those remain explanatory
 * factors until independently approved evidence supports a numerical term.
 */
export function calculateDirectJobsScreening(
  input: DirectJobsScreeningInput,
  catalog: EconomicSourceCatalogSnapshot,
  asOf: Date = new Date(),
): DirectJobsScreeningResult {
  const inputHash = hashEconomicModelInput({ input, catalogVersion: catalog.catalogVersion });
  if (!input.siteId.trim() || !finitePositive(input.load.value)) {
    return emptyResult(input, catalog, 'invalid-input', ['Site id and a finite positive MW value are required.']);
  }

  if (input.operatorProvided) {
    if (!finiteNonNegative(input.operatorProvided.permanentOnsiteFte)
      || !input.operatorProvided.sourceLabel.trim()) {
      return emptyResult(input, catalog, 'invalid-input', ['Operator-provided employment requires a non-negative FTE value and source label.']);
    }
    const value = input.operatorProvided.permanentOnsiteFte;
    return {
      schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
      modelVersion: DIRECT_JOBS_MODEL_VERSION,
      status: 'operator-provided',
      siteId: input.siteId,
      range: { low: value, high: value, unit: 'direct-onsite-fte' },
      evidenceBasis: 'operator-provided',
      method: 'operator-provided',
      facilityOperationsRange: null,
      itAndSiteOperationsRange: null,
      contractedServicesRange: null,
      factors: ['Operator disclosure supersedes the generic screening calculation.'],
      limitations: ['The platform has not independently verified the operator-provided headcount or its payroll/contractor boundary.'],
      modelInputHash: inputHash,
      sourceCatalogVersion: catalog.catalogVersion,
      sourceEntryIds: [],
      trace: [{
        id: 'operator-provided-direct-onsite-fte',
        expression: 'result = operator-provided permanent onsite FTE',
        inputs: { permanentOnsiteFte: value, sourceLabel: input.operatorProvided.sourceLabel },
        output: value,
        sourceEntryIds: [],
      }],
    };
  }

  if (input.load.basis !== 'utility-service') {
    return emptyResult(input, catalog, 'unsupported-mw-basis', [
      `The approved staffing coefficients require utility-service MW; the supplied basis is ${input.load.basis}.`,
      'The engine will not silently treat IT or facility demand as total utility capacity.',
    ]);
  }

  const floorEntry = findRuntimeCoefficient(catalog, 'operations-coverage-floor', {
    permittedUse: 'direct-jobs-screening:facility-operations-floor', asOf,
  });
  const intensityEntry = findRuntimeCoefficient(catalog, 'size-band-intensity', {
    use: 'operations-fte-per-mw',
    permittedUse: 'direct-jobs-screening:operations-size-band',
    asOf,
  });
  if (!floorEntry || !intensityEntry) {
    return emptyResult(input, catalog, 'approved-source-unavailable', [
      'Both a policy-valid 24/7 coverage floor and a policy-valid size-band staffing entry are required.',
      'Proposed, rejected, retired, stale-beyond-policy, or malformed catalog entries cannot produce a runtime number.',
    ]);
  }

  const band = bandForMw(intensityEntry.coefficient.bands, input.load.value);
  if (!band) {
    return emptyResult(input, catalog, 'outside-approved-domain', [
      `${input.load.value} MW falls outside the policy-valid source bands; the engine does not interpolate or extrapolate across unsupported gaps.`,
    ]);
  }

  const floorLow = floorEntry.coefficient.onsitePositionsLow
    * floorEntry.coefficient.weeklyCoverageHours * 52
    / floorEntry.coefficient.annualHoursPerFte;
  const floorHigh = floorEntry.coefficient.onsitePositionsHigh
    * floorEntry.coefficient.weeklyCoverageHours * 52
    / floorEntry.coefficient.annualHoursPerFte;
  const intensityLow = input.load.value * band.low;
  const intensityHigh = input.load.value * band.high;
  const low = roundEconomic(Math.max(floorLow, intensityLow), 1);
  const high = roundEconomic(Math.max(floorHigh, intensityHigh), 1);
  const sourceEntryIds = [floorEntry.id, intensityEntry.id];
  const basisFactor = staffingBasisFactor(input.load.basisTreatment);
  const basisLimitation = staffingBasisLimitation(input.load, 'staffing');

  return {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    modelVersion: DIRECT_JOBS_MODEL_VERSION,
    status: 'calculated',
    siteId: input.siteId,
    range: { low, high, unit: 'direct-onsite-fte' },
    evidenceBasis: 'modelled',
    method: 'coverage-floor-plus-size-band',
    facilityOperationsRange: null,
    itAndSiteOperationsRange: null,
    contractedServicesRange: null,
    factors: [
      ...(basisFactor ? [basisFactor] : []),
      `Operating model: ${input.operatingModel} (explanatory only in v1).`,
      `Workload: ${input.workload} (explanatory only in v1).`,
      'Building count, rack density, cooling architecture, tenant responsibilities, and deployment cadence can change staffing but are not v1 coefficients.',
    ],
    limitations: [
      ...(basisLimitation ? [basisLimitation] : []),
      ...floorEntry.limitations,
      ...intensityEntry.limitations,
      'The source does not support a defensible split between facility operations, IT/site operations, and contracted services, so no component ranges are fabricated.',
      'Direct onsite permanent FTE only; excludes construction, offsite, indirect, induced, and contractor employment unless the underlying disclosure includes them.',
      ...[floorEntry, intensityEntry]
        .filter((entry) => effectiveCatalogLifecycle(entry, asOf) === 'approved-stale')
        .map((entry) => `Approved source entry ${entry.id} is past its next review date and is labeled stale pending refresh.`),
    ],
    modelInputHash: inputHash,
    sourceCatalogVersion: catalog.catalogVersion,
    sourceEntryIds,
    trace: [
      {
        id: 'coverage-floor-low',
        expression: 'positions × weekly coverage hours × 52 ÷ annual hours per FTE',
        inputs: {
          positions: floorEntry.coefficient.onsitePositionsLow,
          weeklyCoverageHours: floorEntry.coefficient.weeklyCoverageHours,
          annualHoursPerFte: floorEntry.coefficient.annualHoursPerFte,
        },
        output: roundEconomic(floorLow, 2),
        sourceEntryIds: [floorEntry.id],
      },
      {
        id: 'coverage-floor-high',
        expression: 'positions × weekly coverage hours × 52 ÷ annual hours per FTE',
        inputs: {
          positions: floorEntry.coefficient.onsitePositionsHigh,
          weeklyCoverageHours: floorEntry.coefficient.weeklyCoverageHours,
          annualHoursPerFte: floorEntry.coefficient.annualHoursPerFte,
        },
        output: roundEconomic(floorHigh, 2),
        sourceEntryIds: [floorEntry.id],
      },
      {
        id: 'size-band-intensity',
        expression: 'utility-service MW × size-band low/high FTE per MW',
        inputs: { mw: input.load.value, band: band.id, lowPerMw: band.low, highPerMw: band.high },
        output: `${roundEconomic(intensityLow, 2)}–${roundEconomic(intensityHigh, 2)} FTE`,
        sourceEntryIds: [intensityEntry.id],
      },
      {
        id: 'binding-range',
        expression: 'max(coverage floor, size-band result) independently for low/high',
        inputs: { floorLow, floorHigh, intensityLow, intensityHigh },
        output: `${low}–${high} direct onsite FTE`,
        sourceEntryIds,
      },
    ],
  };
}

/**
 * Calculates the recorded low and high MW endpoints independently. This is
 * intentionally not a midpoint estimate or an interpolation: source bands can
 * change inside the site range, so each endpoint retains its own method trace.
 */
export function calculateDirectJobsMwRangeScreening(
  input: DirectJobsScreeningInput,
  loadRange: MwRangeQuantity,
  catalog: EconomicSourceCatalogSnapshot,
  asOf: Date = new Date(),
): DirectJobsMwRangeScreeningResult {
  const validRange = finitePositive(loadRange.low)
    && finitePositive(loadRange.high)
    && loadRange.high >= loadRange.low;
  const endpointLoad = (value: number) => ({
    value,
    basis: loadRange.basis,
    phaseBasis: loadRange.phaseBasis,
    ...(loadRange.sourceBasis ? { sourceBasis: loadRange.sourceBasis } : {}),
    basisTreatment: loadRange.basisTreatment,
    ...(loadRange.basisSource ? { basisSource: loadRange.basisSource } : {}),
    ...(loadRange.basisConversionPue ? { basisConversionPue: loadRange.basisConversionPue } : {}),
  });
  const lowEndpoint = calculateDirectJobsScreening({
    ...input,
    load: endpointLoad(loadRange.low),
  }, catalog, asOf);
  const highEndpoint = validRange
    ? calculateDirectJobsScreening({
        ...input,
        load: endpointLoad(loadRange.high),
      }, catalog, asOf)
    : calculateDirectJobsScreening({
        ...input,
        load: endpointLoad(Number.NaN),
      }, catalog, asOf);
  const calculatedCount = Number(lowEndpoint.range != null) + Number(highEndpoint.range != null);
  const sourceEntryIds = [...new Set([
    ...lowEndpoint.sourceEntryIds,
    ...highEndpoint.sourceEntryIds,
  ])];
  const limitations = [
    'The low and high MW endpoints are screened independently; no midpoint is presented as a recorded site fact.',
    'Endpoint results are not an interpolated range and may use different policy-valid coefficient bands.',
    ...(staffingBasisLimitation(loadRange, 'staffing')
      ? [staffingBasisLimitation(loadRange, 'staffing')!]
      : []),
  ];
  return {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    modelVersion: DIRECT_JOBS_MW_RANGE_MODEL_VERSION,
    status: calculatedCount === 2
      ? 'calculated'
      : calculatedCount === 1 ? 'partially-calculated' : 'not-calculated',
    siteId: input.siteId,
    loadRange,
    lowEndpoint,
    highEndpoint,
    sourceEntryIds,
    limitations,
    modelInputHash: hashEconomicModelInput({ input, loadRange, catalogVersion: catalog.catalogVersion }),
    sourceCatalogVersion: catalog.catalogVersion,
  };
}
